import { startGame } from '@sl/base/main';
import { Scene } from '@sl/base/scene';
import { Bonus } from '@sl/games/fierce_vikings/bonus';
import { Payline } from '@sl/base/logic/payline';
import { Winning } from '@sl/base/logic/winning';

class GameScene extends Scene {
    update() {
        super.update();
    }

    init() {
        super.init();

        this.letInitialize(() => {
            this.winTable = undefined;
            this.winText = undefined;
            this.amountWonStep = 15;
            this.amountWonTimeout = 40;
            this.amountWonFadeInSpeed = 300;

            this.positionerContainer = this.add.container();
            this.createBg();

            this.slots.symbolWidth = 130;
            this.slots.symbolHeight = 140;
            this.slots.spinningRowsCount = 6;
            this.slots.symbolsOffsetLeft = 127;
            this.slots.visibleRowsCount = 3;
            this.slots.frameMaskOffset = 40;
            this.slots.reelsOffsetTop = 355;
            this.slots.symbolBlurFrame = 0;
            this.slots.frameTopOffset = -30;
            this.slots.frameLeftOffset = 0;
            this.slots.spaceBetweenReels = -6;
            this.slots.symbolsFade = 'symbols_fade';
            this.slots.reelBackgroundVerticalPadding = -3;
            this.slots.overspinSound = this.sound.add('overspin', {"loop":false, "volume": 1});

            this.slots.createSlotMachine();
            this.addGameLogo();
            this.addSymbolsAnimations();

            this.bonus = new Bonus(this);
            this.payline = new Payline(this);
            this.winning = new Winning(this);

            this.payline.drawPaylines();
            this.outsideFrame = this.add.sprite(this.slots.frame.x, this.slots.frame.y, 'outside_frame').setOrigin(0.5, 0.5);
            this.slots.frameContainerFront.add(this.outsideFrame);

            this.spinSound = this.sound.add('spin', {"loop":true, "volume": 1});

            this.createWinningsContainer();
            this.spritesReady = true;
            this.baseGamePositioner();
        });
    }

    afterPurchase() {
        this.winning.removeTimers();
        this.slots.spin();
    }

    afterStep() {
        const { scenario } = this.request;
        
        if (scenario) {
            if(!this.skip && scenario.freespins && parseInt(scenario.freespins.current) === 1) {
                this.bonus.init(() => {
                    this.afterPurchase();
                });
            } else {
                if (scenario.reels) {
                    this.afterPurchase();
                } 
            }

            return;
        }

        if(this.request.previousScenario && this.request.previousScenario.freespins) {
            this.bonus.after(() => {
                this.request.spinFinished();
            });

            return;
        }

        this.request.spinFinished();
    }

    overspinAnimationConfig(overspinReel) {
        var curve = new Phaser.Curves.Path(0, 0);

        curve.lineTo(this.slots.symbolWidth, 0);
        curve.lineTo(this.slots.symbolWidth, this.slots.frameMaskHeight);
        curve.lineTo(0, this.slots.frameMaskHeight);
        curve.lineTo(0, 0);
        
        return {
            x: overspinReel.x - this.slots.symbolWidth / 2,
            y: overspinReel.y - this.slots.frameMaskHeight / 2,
            blendMode: 'ADD',
            gravityY: 300,
            lifespan: 2000,
            quantity: 3,
            accelerationX: -5,
            rotate: { min: -180, max: 180 },
            scale: { start: 0.5, end: 0 },
            alpha: { min: 0.2, max: 0.5 },
            emitZone: {
                source: curve,
                type: 'random',
                quantity: 5
            }
        }
    }

    createWinningsContainer() {
        this.winningsContainer = this.add.container();
        this.winningsContainer.alpha = 0;
    }

    createBg() {
        this.bg = this.add.sprite(0, 0, 'bg').setOrigin(0.5, 0);
        this.positionerContainer.add(this.bg);

        this.bgLit = this.add.sprite(0, 0, 'bgLit').setOrigin(0.5, 0);
        this.positionerContainer.add(this.bgLit);
        this.bgLit.alpha = 0;

        this.lightnings = [
            this.add.sprite(0, 0, 'lightning_front'),
            this.add.sprite(0, 0, 'lightning_front_alt'),
            this.add.sprite(0, 0, 'lightning_back'),
            this.add.sprite(0, 0, 'lightning_back_alt'),
        ];

        for (const item of this.lightnings) {
            item.alpha = 0;
            item.lightningAnimation = this.createLightningAnimation(item);
        }

        this.bgLightningAnimation = this.createLightningAnimation(this.bgLit);

        this.lightningAfter = 5;
        this.loopLightning();
    }

    loopLightning() {
        this.time.delayedCall(this.lightningAfter * 1000, () => {
            const rnd = Phaser.Math.Between(1, 10);

            this.lightningAfter = this.lightningAfter > 15 ? Phaser.Math.Between(6, 10) : Phaser.Math.Between(13, 17);

            this.playLightning();
            this.loopLightning();

            if(rnd > 9) {
                this.time.delayedCall(100, () => {
                    this.playLightning();
                });
            }

            if(rnd > 10) {
                this.time.delayedCall(150, () => {
                    this.playLightning();
                });
            }
        });
    }

    playLightning() {
        let item;

        do {
            item = Phaser.Math.RND.pick(this.lightnings);
        } while (item.isDisabled)

        item.lightningAnimation.play();
        this.bgLightningAnimation.play();
    }

    createLightningAnimation(target) {
        const animation = {};

        animation.lightningLightUp = this.tweens.add({
            targets: target,
            alpha: { from: 0, to: 0.7 },
            ease: 'Power2',
            duration: 250,
            paused: true,
        });

        animation.lightningLightMiddleDown = this.tweens.add({
            targets: target,
            alpha: { from: 0.7, to: 0 },
            ease: 'Power2',
            duration: 50,
            paused: true,
        });

        animation.lightningLightToFull = this.tweens.add({
            targets: target,
            alpha: { from: 0, to: 1 },
            ease: 'Power2',
            duration: 25,
            paused: true,
        });

        animation.lightningLightDown = this.tweens.add({
            targets: target,
            alpha: { from: 1, to: 0 },
            ease: 'Power2',
            duration: 1000,
            paused: true,
        });

        animation.play = () => {
            this.sound.play('thunder');
            this.time.delayedCall(1200, () => animation.lightningLightUp.play());
        }

        animation.lightningLightUp.on('complete', () => {
            animation.lightningLightMiddleDown.play();
        });

        animation.lightningLightMiddleDown.on('complete', () => {
            this.time.delayedCall(50, () => animation.lightningLightToFull.play());
        });

        animation.lightningLightToFull.on('complete', () => {
            animation.lightningLightDown.play();
        });

        return animation;
    }

    addGameLogo() {
        this.logo = this.add.sprite(0, 0, 'logo').setOrigin(0.5, 0.5);

        this.anims.create({
            key: 'logo',
            frameRate: 20,
            yoyo: 0,
            repeat: -1,
            repeatDelay: 3000,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('logo', {
                start: 0,
                end: 0,
            }),
        });

        this.anims.play('logo', this.logo);
    }

    afterSpin() {
        this.request.step();
    }

    addSymbolsAnimations() {
        for(const [ reelIndex, reel ] of Object.entries(this.slots.reelsContainersObjects)) {
            for(const symbolIndex of Object.keys(reel.symbols)) {
                let symbol = reel.symbols[symbolIndex];
                var config = {
                    key: `${symbol.texture.key}`,
                    skipMissedFrames: true,
                    frames: this.anims.generateFrameNumbers(`${symbol.texture.key}`, {
                        start: 0,
                        end: 24,
                    }),
                    frameRate: 20,
                    yoyo: 0,
                    repeat: 0
                };
                this.anims.create(config);
                symbol.anims.load(`${symbol.texture.key}`);
            }
        }
    }

    showWinning(text, callback) {
        let win = 'Mega';

        if(!text) {
            var winningAmount = this.request.scenario.amount_won;

            if(!winningAmount) {
                this.winning.playPaylines();

                if(callback) {
                    callback();
                }

                return;
            }

            win = 'Regular';

            if(this.request.scenario.tags.big_win) {
                win = 'Big';
            }
        }

        this.prepareWinningContainer(win);

        this.winning.playPaylines();

        if(callback) {
            callback();
        }
    }

    customPaylineSound(symbolObject) {
        if(symbolObject.symbolName === 'scatter') {
            this.sound.play('Mega');
        } else {
            this.playWinlineSound();
        }
    }

    customStopAnimation(reelIndex) {
        this.sound.play('reel_stop');
    }

    winningOnPayline(payline, match) {
        this.destroyWinningOnPayline();

        if(match.type !== 'row') {
            return;
        }

        const symbol = this.slots.getSymbolOnReelPosition(3, payline.midPosition)

        let winning = General.format_currency(match.multiplier * Config.current_amount.amount).replace(Config.currency.symbol, '');

        let amountContainer = this.winning.createWinningFromSprites(winning, 0.4);

        amountContainer.x = symbol.x;
        amountContainer.y = symbol.y;

        amountContainer.name = 'win_amount';
        amountContainer.setScale(0);

        this.slots.reelsContainer.add(amountContainer);
        
        amountContainer.tween = this.tweens.add({
            targets: [amountContainer],
            scale: 1,
            ease: 'Back',
            duration: 300
        });
    }

    destroyWinningOnPayline() {
        let winAmount = this.slots.reelsContainer.getByName('win_amount');

        if(winAmount) {
            winAmount.destroy();
        }
    }

    prepareWinningContainer() {
        this.hideWinTable();

        this.winningsContainer.alpha = 0;
    }

    hideWinTable() {
        if(this.winningsContainer && this.winningsContainer.alpha) {
            if(this.winningsContainer.tween && this.winningsContainer.tween.isPlaying()) {
                this.winningsContainer.tween.stop();
                this.winningsContainer.tween.remove();
            }
        
            this.winningsContainer.alpha = 0;

            for(const [symbolIndex, sprite] of Object.entries(this.winningsContainer.getAll())) {
                if(sprite.tween && sprite.tween.isPlaying()) {
                    sprite.tween.stop();
                    sprite.tween.remove();
                }
            }
        }
    }

    drawPaylines() {
        const halfSymbolHeight = this.slots.symbolHeight / 2;
        const offsetY = this.slots.frame.y;
        const x = this.slots.frame.x - this.slots.frameLeftOffset - 1;

        this.payline.paylines[1] = this.add.sprite(x, offsetY, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[1].midPosition = 1;
        this.payline.paylines[2] = this.add.sprite(x, offsetY - this.slots.symbolHeight, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[2].midPosition = 0;
        this.payline.paylines[3] = this.add.sprite(x, offsetY + this.slots.symbolHeight, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[3].midPosition = 2;
        this.payline.paylines[4] = this.add.sprite(x, offsetY, 'payline_04').setOrigin(0.5, 0.5);
        this.payline.paylines[4].midPosition = 2;
        this.payline.paylines[5] = this.add.sprite(x, offsetY, 'payline_05').setOrigin(0.5, 0.5);
        this.payline.paylines[5].midPosition = 0;
        this.payline.paylines[6] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_06_09').setOrigin(0.5, 0.5);
        this.payline.paylines[6].midPosition = 2;
        this.payline.paylines[7] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_07_08').setOrigin(0.5, 0.5);
        this.payline.paylines[7].midPosition = 0;
        this.payline.paylines[8] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_07_08').setOrigin(0.5, 0.5);
        this.payline.paylines[8].midPosition = 1;
        this.payline.paylines[9] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_06_09').setOrigin(0.5, 0.5);
        this.payline.paylines[9].midPosition = 1;
        this.payline.paylines[10] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_10_18').setOrigin(0.5, 0.5);
        this.payline.paylines[10].midPosition = 1;
        this.payline.paylines[11] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_11_19').setOrigin(0.5, 0.5);
        this.payline.paylines[11].midPosition = 1;
        this.payline.paylines[12] = this.add.sprite(x, offsetY, 'payline_12').setOrigin(0.5, 0.5);
        this.payline.paylines[12].midPosition = 0;
        this.payline.paylines[13] = this.add.sprite(x, offsetY, 'payline_13').setOrigin(0.5, 0.5);
        this.payline.paylines[13].midPosition = 2;
        this.payline.paylines[14] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_14_21').setOrigin(0.5, 0.5);
        this.payline.paylines[14].midPosition = 0;
        this.payline.paylines[15] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_15_20').setOrigin(0.5, 0.5);
        this.payline.paylines[15].midPosition = 2;
        this.payline.paylines[16] = this.add.sprite(x, offsetY, 'payline_16').setOrigin(0.5, 0.5);
        this.payline.paylines[16].midPosition = 0;
        this.payline.paylines[17] = this.add.sprite(x, offsetY, 'payline_17').setOrigin(0.5, 0.5);
        this.payline.paylines[17].midPosition = 2;
        this.payline.paylines[18] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_10_18').setOrigin(0.5, 0.5);
        this.payline.paylines[18].midPosition = 2;
        this.payline.paylines[19] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_11_19').setOrigin(0.5, 0.5);
        this.payline.paylines[19].midPosition = 0;
        this.payline.paylines[20] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_15_20').setOrigin(0.5, 0.5);
        this.payline.paylines[20].midPosition = 1;
        this.payline.paylines[21] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_14_21').setOrigin(0.5, 0.5);
        this.payline.paylines[21].midPosition = 1;
        this.payline.paylines[22] = this.add.sprite(x, offsetY, 'payline_22').setOrigin(0.5, 0.5);
        this.payline.paylines[22].midPosition = 2;
        this.payline.paylines[23] = this.add.sprite(x, offsetY, 'payline_23').setOrigin(0.5, 0.5);
        this.payline.paylines[23].midPosition = 0;
        this.payline.paylines[24] = this.add.sprite(x, offsetY, 'payline_24').setOrigin(0.5, 0.5);
        this.payline.paylines[24].midPosition = 1;
        this.payline.paylines[25] = this.add.sprite(x, offsetY, 'payline_25').setOrigin(0.5, 0.5);
        this.payline.paylines[25].midPosition = 1;
        
        for(const [ paylineIndex, payline ] of Object.entries(this.payline.paylines)) {
            payline.name = `payline_${paylineIndex}`;
            this.payline.paylinesContainer.add(payline);
            this.slots.reelsContainer.add(payline);
        }

        this.payline.paylinesContainer.y = this.slots.reelsOffsetTop;
        this.payline.hidePaylines();
    }

    playSpinSound() {
        this.spinSound.play();
    }

    muteSpinSound() {
        this.spinSound.stop();
    }

    playStopSound() {
        this.sound.play('click', {"volume":0.4});
    }

    playWinlineSound() {
        this.sound.play('gem');
    }

    skipAnimations() {
        this.winning.removeTimers();
        this.hideWinTable();
        this.skip = true;
        this.winning.doCallback();
    }

    symbolsFader() {
        const fader = this.slots.reelsContainer.getByName(this.slots.symbolsFade);
        fader.alpha = 1;
        this.slots.reelsContainer.bringToTop(fader);
    }

    destroySymbolsFader() {
        this.slots.reelsContainer.getByName(this.slots.symbolsFade).alpha = 0;
    }

    gamePositioner() {
        if(!this.spritesReady) {
            return;
        }

        this.logo.x = this.slots.frame.x + this.cameras.main.centerX;
        this.logo.y = 90 + this.slots.frameTopOffset;

        let offsetX = 0;

        if(this.orientationIsSetToPortrait()) {
            this.bgLit.setTexture('bgLit_portrait')
            this.bg.setTexture('bg_portrait');
            this.slots.frameContainer.x = this.cameras.main.centerX + offsetX;
            this.slots.symbolsAndPaylinesContainer.x = this.cameras.main.centerX + offsetX;
            this.slots.reelsContainer.x = this.cameras.main.centerX + offsetX;

            this.slots.frameMaskHeight = 410;
            this.slots.frameContainer.setScale(1.05);
            this.slots.symbolsAndPaylinesContainer.setScale(1.05);
            this.slots.reelsContainer.setScale(1.05);

            this.lightnings[0].setX(0).setY(0).setOrigin(0).setTexture('lightning_portrait_left');
            this.lightnings[1].setX(910).setY(0).setOrigin(1, 0).setTexture('lightning_portrait_right');
            this.lightnings[2].setX(910).setY(0).setOrigin(0.5, 0).setTexture('lightning_portrait_top');
            this.lightnings[3].isDisabled = true;
        } else {
            this.slots.frameMaskHeight = 410;
            this.slots.frameContainer.setScale(1);
            this.slots.symbolsAndPaylinesContainer.setScale(1);
            this.slots.reelsContainer.setScale(1);
            this.bg.setTexture('bg');
            this.bgLit.setTexture('bgLit')

            this.lightnings[0].setX(0).setY(0).setOrigin(0).setTexture('lightning_front');
            this.lightnings[1].setX(1366).setY(0).setOrigin(1, 0).setTexture('lightning_front_alt');
            this.lightnings[2].setX(1125).setY(0).setOrigin(0.5, 0).setTexture('lightning_back');
            this.lightnings[3].setX(800).setY(0).setOrigin(0.5, 0).setTexture('lightning_back_alt').isDisabled = false;
        }


        this.slots.createReelsMask(offsetX);
    }
}

startGame(GameScene);